import React from 'react';
import { WithStyles, withStyles } from '@mui/styles';
import { ClassNameMap } from '@mui/styles/withStyles';

import { Divider, Grid, Typography, TextField, Button, Box, FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@mui/material';
import { Assignment, CheckCircleOutlined, Delete, ErrorOutlineOutlined } from '@mui/icons-material';

import PathwayView from '../../../../../../components/PathwayBuilder/View';
import SpecialityCard from '../../../../../../components/SpecialityCard';
import GridActionCard from '../../../../../../components/Grid/GridActionCard';

import { Pot, PotsReducerAction, ValidationErrors as PotErrors, enumSampleTypeDescriptions } from '../../pots';
import { Category } from '../../speciality';
import { ValidationErrors as SpecialityPathwayErrors } from '../../speciality_pathway';
import Pathway, { EnumPathwayStepComopnentType } from '../../../../../../components/PathwayBuilder/Common';
import { UnknownObject } from '../../../../../../react-app-env';

import styles from './styles';

interface Props extends WithStyles<typeof styles> {
  classes: ClassNameMap<string>;
  category: Category;
  pots: Pot[];
  potPathway: Pathway;
  potErrors?: Record<string, PotErrors>;
  specialityPathway: Pathway;
  specialityPathwayValues: UnknownObject;
  specialityPathwayErrors: Record<string, SpecialityPathwayErrors>;
  selectedMode: string | null;
  dispatchSpecialityPathway: React.Dispatch<{ type: string; specialityCategoryId: string; id: string; value: string }>;
  potDispatch: React.Dispatch<{ type: PotsReducerAction; value: any }>;
  validate: () => void;
  setSelectedMode: (mode: string | null) => void;
}

const SamplesStep = ({
  classes,
  category,
  pots,
  potPathway,
  potErrors,
  specialityPathway,
  specialityPathwayValues,
  specialityPathwayErrors,
  selectedMode,
  dispatchSpecialityPathway,
  potDispatch,
  validate,
  setSelectedMode,
}: Props): React.ReactElement => {
  const selectedPot = pots.find((pot) => pot.id === selectedMode);

  const handleAddPot = () => {
    potDispatch({ type: PotsReducerAction.ADD_POT, value: { speciality_category_id: category.id } });
  };

  const renderSurgicalPathway = () => (
    <>
      <Box display="flex" alignItems="center" justifyContent={'space-between'} ml={2} mb={2}>
        <Typography variant="h4">Clinical Information</Typography>
      </Box>
      <PathwayView
        pathway={specialityPathway}
        values={specialityPathwayValues.pathway}
        validate={validate}
        dispatch={(value) => dispatchSpecialityPathway({ ...value, specialityCategoryId: category.id })}
      />
      <TextField
        fullWidth
        className={classes.notes}
        data-qa="notes-textfield"
        label="Additional information"
        variant="outlined"
        value={specialityPathwayValues.notes}
        multiline
        rows={4}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          dispatchSpecialityPathway({ type: 'NOTES', id: '', specialityCategoryId: category.id, value: event.target.value })
        }
      />
    </>
  );

  const renderPotContent = () => {
    if (!selectedPot)
      return (
        <Typography ml={2} variant="subtitle1">
          Sample not selected
        </Typography>
      );

    const handlePotChange = (id: string, value: string) => {
      const updatedPathway = { ...selectedPot.pathway, [id]: value };
      potDispatch({ type: PotsReducerAction.UPDATE_POT, value: { ...selectedPot, pathway: updatedPathway } });
    };

    const handlePotNotes = (value: string) => {
      potDispatch({ type: PotsReducerAction.UPDATE_POT, value: { ...selectedPot, notes: value } });
    };

    return (
      <>
        <Box ml={2} mb={2} display={'flex'} justifyContent={'space-between'}>
          <Typography variant="h5">{selectedPot.name || ''}</Typography>
          {pots.length > 1 && (
            <Button startIcon={<Delete />} variant="outlined" onClick={() => potDispatch({ type: PotsReducerAction.DELETE_POT, value: { id: selectedPot.id } })}>
              Delete Sample
            </Button>
          )}
        </Box>
        <Box pl={2} mb={3}>
          <FormControl fullWidth variant="outlined" required>
            <InputLabel htmlFor={`sample-type-select`}>Sample Type</InputLabel>
            <Select
              id={`sample-type-select`}
              label={'Sample Type'}
              value={selectedPot.sample_type}
              onBlur={validate}
              onChange={(event) => potDispatch({ type: PotsReducerAction.UPDATE_POT, value: { ...selectedPot, sample_type: event.target.value } })}>
              {Object.entries(enumSampleTypeDescriptions).map(([id, label]) => (
                <MenuItem key={id} value={id}>
                  {label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText className={classes.description}>Choose the sample type</FormHelperText>
          </FormControl>
        </Box>
        <PathwayView pathway={potPathway} values={selectedPot.pathway} validate={validate} dispatch={({ id, value }) => handlePotChange(id, value)} />
        <TextField
          fullWidth
          className={classes.notes}
          data-qa="notes-textfield"
          label="Additional information"
          variant="outlined"
          value={selectedPot.notes}
          multiline
          rows={4}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => handlePotNotes(event.target.value)}
        />
      </>
    );
  };

  return (
    <div className={classes.root}>
      <Typography mb={2} variant="h4">
        Samples
      </Typography>
      <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
        <Typography mb={2} variant="h6">
          {category.name}
        </Typography>
        {selectedMode === 'surgical' && (!!specialityPathwayErrors?.[category.id]?.incomplete || !!specialityPathwayErrors?.[category.id]?.characterLimitExceeded) && (
          <Typography variant="body2" color="error">
            {specialityPathwayErrors[category.id].incomplete || specialityPathwayErrors[category.id].characterLimitExceeded}
          </Typography>
        )}

        {selectedMode !== 'surgical' && !!potErrors?.[category.id]?.incomplete && (
          <Typography variant="body2" color="error">
            {potErrors[category.id].incomplete}
          </Typography>
        )}
      </Box>
      <Grid container spacing={1}>
        <Grid item xs={4}>
          <div className={classes.pots}>
            <div className={classes.potsList}>
              {/* Surgical Pathway Card */}
              <SpecialityCard
                small
                gridSize={12}
                muiIcon={<Assignment sx={{ fill: (theme) => theme.palette.grey[700] }} />}
                title="Clinical details"
                selected={selectedMode === 'surgical'}
                description={specialityPathway?.steps
                  .map((step) => {
                    const value = specialityPathwayValues?.pathway?.[step.id];
                    if (!value) return null;
                    if (step.component_type === EnumPathwayStepComopnentType.FREETEXT) {
                      return value.length > 50 ? `${value.substring(0, 50)}...` : value;
                    }
                    return step.options.find((opt) => opt.id === value)?.label;
                  })
                  .filter((x) => x)
                  .join(', ')}
                statusIcon={
                  specialityPathwayValues?.pathway && Object.keys(specialityPathwayValues.pathway).length >= specialityPathway?.steps?.filter((s) => s.mandatory).length ? (
                    <CheckCircleOutlined />
                  ) : (
                    <ErrorOutlineOutlined />
                  )
                }
                onClick={() => setSelectedMode('surgical')}
              />
              <Typography variant="overline">Samples</Typography>
              {/* Pot Cards */}
              {pots.map((pot) => (
                <SpecialityCard
                  key={pot.id}
                  small
                  gridSize={12}
                  chipLabel={enumSampleTypeDescriptions[pot.sample_type]}
                  title={pot.name}
                  description={potPathway?.steps
                    .map((step) => {
                      const value = pot.pathway[step.id];
                      return value ? step.options.find((opt) => opt.id === value)?.label : null;
                    })
                    .filter(Boolean)
                    .join(', ')}
                  statusIcon={
                    pot.sample_type && Object.keys(pot.pathway).length >= potPathway?.steps.filter((s) => s.mandatory).length ? <CheckCircleOutlined /> : <ErrorOutlineOutlined />
                  }
                  selected={selectedMode === pot.id}
                  onClick={() => potDispatch({ type: PotsReducerAction.SELECT_POT, value: { id: pot.id } })}
                />
              ))}

              {/* Add Pot Card */}
              <GridActionCard small gridSize={12} label="Add Sample" onClick={handleAddPot} />
            </div>
            <Divider orientation="vertical" />
          </div>
        </Grid>

        <Grid item xs={8}>
          {selectedMode === 'surgical' ? renderSurgicalPathway() : renderPotContent()}
        </Grid>
      </Grid>
    </div>
  );
};

export default withStyles(styles)(SamplesStep);
